import type { FC } from "react";
import { translate } from "app/filters/i18nFilter";
import { SWReactIcons } from "@similarweb/icons";
import { openHooksModalV5 } from "app/services/ModalService";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { useExceededLimitation } from "app/components/metering/UseLimitation";
import { isIndustryAnalysisLimitationExceeded } from "pro-features/pages/module start page/src/industry analysis/utils/metering/IndustryAnalysisMetering";
import InteractiveTooltip from "app/components/React/Tooltip/InteractiveTooltip/InteractiveTooltip";
import { MARKET_ANALYSIS_HOOK, INDUSTRY_ANALYSIS_METERING_HOOK, HELP_TOOLTIP_WIDTH, DISABLED_TOOLTIP } from "./consts";
import { TooltipWrapper, StyledTooltipHeader, StyledButton } from "./StyledComponents";
import I18n from "pro-features/components/WithTranslation/src/I18n";

interface IConvertIntoIndustryBtn {
    isDisabled?: boolean;
    segmentGroupProps: { id: string; name: string };
    onClick: (segmentGroupProps: { id: string; name: string }) => void;
    disabledTooltip?: string;
}

const ConvertIntoIndustryBtn: FC<IConvertIntoIndustryBtn> = ({
    isDisabled,
    onClick,
    segmentGroupProps = { id: "", name: "" },
    disabledTooltip = DISABLED_TOOLTIP,
}) => {
    const isLimitationExceeded = useExceededLimitation(isIndustryAnalysisLimitationExceeded);
    const isLocked = !isAvailable(swSettings.components["WebMarketResearchOverviewHome"]);
    const isHooked = isLimitationExceeded || isLocked;
    const buttonType = isHooked ? "trial" : "primary";
    const iconName = isHooked ? "sparkles-gray" : "lightning";
    const tooltipText = isDisabled ? disabledTooltip : "segments.page.comparison.convert.into.industry.tooltip";
    const tooltipTextBold = "segments.page.comparison.convert.into.industry.tooltip.bold";

    const getHookModalType = () => {
        if (isLocked) {
            return MARKET_ANALYSIS_HOOK;
        } else if (isLimitationExceeded) {
            return INDUSTRY_ANALYSIS_METERING_HOOK;
        }
    };

    const onBtnClick = () => {
        if (isDisabled) {
            return;
        }

        const hookModalType = getHookModalType();
        if (hookModalType) {
            openHooksModalV5(hookModalType);
        } else {
            onClick(segmentGroupProps);
        }
    };

    const tooltipContent = (
        <TooltipWrapper>
            {!isDisabled && <StyledTooltipHeader>{tooltipTextBold}</StyledTooltipHeader>}
            <I18n>{tooltipText}</I18n>
        </TooltipWrapper>
    );

    return (
        <div data-automation="ConvertIntoIndustryBtn">
            <InteractiveTooltip
                maxWidth={HELP_TOOLTIP_WIDTH}
                tooltipContent={tooltipContent}
                placement="bottom"
            >
                <div>
                    <StyledButton
                        onClick={isDisabled ? undefined : onBtnClick}
                        type={buttonType}
                        isDisabled={isDisabled}
                    >
                        <SWReactIcons
                            iconName={iconName}
                            size="xs"
                        />
                        {translate("segments.page.comparison.convert.into.industry")}
                    </StyledButton>
                </div>
            </InteractiveTooltip>
        </div>
    );
};

export default ConvertIntoIndustryBtn;
